import { useMemo } from "react";
import type { ErrorLike } from "@apollo/client";

import { AGE_STATUS } from "#app/lib/age-status";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { combineWhereConditions } from "#app/lib/query-utils";
import type {
  DatePresetKey,
  DatePresetValue,
  ResolvedDateRange,
} from "#app/lib/utils/date-presets";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { useScopedActivityWhere } from "#app/widgets/dashboards/scope/use-scoped-activity-where";
import { useWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useActivityGroupCounts } from "../shared/use-activity-group-counts";
import { previousWindow } from "../shared/comparison-window";

type UseStatStripOptions = {
  /** The strip's own window preset — `filters.dateRange` beats it when set. */
  window: DatePresetKey;
  showActive: boolean;
  showAttention: boolean;
  showClosed: boolean;
  showUpdates: boolean;
  filters?: unknown;
};

export type StatStripData = {
  /** Now-state: projects currently in a pending/in-progress stage. */
  active: number;
  /** The worked subset of `active` — the rest are still pending. */
  activeInProgress: number;
  /** Now-state: projects currently Update soon or Stale (`atRisk` / `offTrack`). */
  attention: number;
  /** The two tiers `attention` sums. Reported separately because a headline
   *  count and one of its tiers are the same number whenever the other tier is
   *  empty — the caption needs both to know whether it has a split to show. */
  attentionAtRisk: number;
  attentionOffTrack: number;
  /** Windowed: projects created in the window that are now in a Done stage. */
  closed: number;
  /** Windowed: posted Updates on projects in the window (the scope pins the
   *  actor). The sum of `updateCounts`, so the figure and its drill agree by
   *  construction. */
  updates: number;
  /** Posted Updates per project in the window, keyed by issue id — the rows
   *  the Updates drill lists and the figure it shows beside each. A project
   *  with no Update in the window has no entry. */
  updateCounts: Record<string, number>;
  /** The same count over the previous equal-length window; null on all-time,
   *  where there is no previous window. The comparison is left to the reader —
   *  two counts side by side carry both the trend and its scale, which a
   *  signed difference alone does not. */
  closedPrev: number | null;
  updatesPrev: number | null;
  /** Stage id sets for the drill slices. */
  openIds: string[];
  doneIds: string[];
  /** The folded now-state `where` (scope + card Filters, NO date clause) — the
   *  base every stat's drill slice ANDs onto. Each windowed stat's slice
   *  carries its own exact bounds, so folding a window here would narrow a
   *  now-state drill to rows the stat never counted. */
  nowWhere: Record<string, unknown>;
  /** The effective flow window — key for copy, resolved range for slice bounds. */
  windowKey: DatePresetKey;
  windowRange: ResolvedDateRange;
  loading: boolean;
  error: ErrorLike | undefined;
};

/**
 * The stat strip's count reader: every number is a SHARED server aggregation
 * (`issueGroupCounts` / `activityGroupCounts`) over the ambient scope — nothing
 * is computed from rows client-side, and nothing new is asked of the API.
 *
 * Two window semantics, deliberately split:
 * - **Now-state** (Active, Needs attention) passes `dateField: null` — the
 *   current book of business. A windowed "needs attention" would hide exactly
 *   the most lapsed projects (untouched for weeks, so outside every window).
 * - **Flow** (Completed, Updates) runs on the strip's own window (`window`
 *   config, overridden by `filters.dateRange`), never the toolbar's — the
 *   headline "Completed this week" must not silently change meaning with the
 *   toolbar pill. Each flow stat runs a second, equal-length previous-window
 *   query for its comparison count; on all-time there is no previous window
 *   and those queries are skipped.
 *
 * Toggled-off stats skip their queries by passing undefined where/groupBy — a
 * skipped `useIssueGroupCounts` never flips `ready`, so the loading fold gates
 * each query on the same condition that runs it.
 */
export function useStatStrip(options: UseStatStripOptions): StatStripData {
  const { stages } = useWorkspaceData();
  const openIds = useMemo(
    () =>
      stages
        .filter((s) => s.type === "pending" || s.type === "inProgress")
        .map((s) => s.stageId),
    [stages],
  );
  const inProgressIds = useMemo(
    () => stages.filter((s) => s.type === "inProgress").map((s) => s.stageId),
    [stages],
  );
  const doneIds = useMemo(
    () => stages.filter((s) => s.type === "done").map((s) => s.stageId),
    [stages],
  );

  const { base, filters } = useWidgetFilters(options.filters);
  const windowValue: DatePresetValue = filters.dateRange ?? { key: options.window };

  const { where: nowWhere } = useScopedWhere(base, { dateField: null });

  const wantActive = options.showActive && openIds.length > 0;
  const activeCounts = useIssueGroupCounts({
    where: wantActive ? nowWhere : undefined,
    groupBy: wantActive ? "stageId" : undefined,
  });

  const attentionCounts = useIssueGroupCounts({
    where: options.showAttention ? nowWhere : undefined,
    groupBy: options.showAttention ? "age.status" : undefined,
  });

  const closedBase = useMemo(
    () =>
      doneIds.length > 0
        ? (combineWhereConditions([base, { stageId: { in: doneIds } }]) ?? {})
        : undefined,
    [base, doneIds],
  );
  const {
    where: closedWhere,
    dateRange: windowRange,
    scope: windowScope,
  } = useScopedWhere(closedBase, { override: { dateRange: windowValue } });
  const windowKey = windowScope.dateRange.key;
  const hasPrevious = windowKey !== "all-time";

  const wantClosed = options.showClosed && doneIds.length > 0;
  const closedCounts = useIssueGroupCounts({
    where: wantClosed ? closedWhere : undefined,
    groupBy: wantClosed ? "stageId" : undefined,
  });

  const prevOverride = useMemo(
    () => ({ dateRange: { key: "custom" as const, custom: previousWindow(windowRange) } }),
    [windowRange],
  );
  const { where: prevClosedWhere } = useScopedWhere(closedBase, { override: prevOverride });
  const wantClosedPrev = wantClosed && hasPrevious;
  const prevClosedCounts = useIssueGroupCounts({
    where: wantClosedPrev ? prevClosedWhere : undefined,
    groupBy: wantClosedPrev ? "stageId" : undefined,
  });

  const { where: updatesWhere } = useScopedActivityWhere(null, {
    override: { dateRange: windowValue },
  });
  // Posted Updates per project: ONE aggregation serves the headline (its sum)
  // and the drill (its rows), so the two cannot disagree. It counts events,
  // never projects, so the now-state issue predicate is not folded — an actor
  // narrowing is the analog the activity fold applies.
  const updatesCounts = useActivityGroupCounts({
    groupBy: "update.issue",
    where: updatesWhere,
    skip: !options.showUpdates,
  });

  const { where: prevUpdatesWhere } = useScopedActivityWhere(null, { override: prevOverride });
  const wantUpdatesPrev = options.showUpdates && hasPrevious;
  const prevUpdatesCounts = useActivityGroupCounts({
    groupBy: "update.issue",
    where: prevUpdatesWhere,
    skip: !wantUpdatesPrev,
  });

  const sumIds = (counts: Record<string, number>, ids: string[]) =>
    ids.reduce((sum, id) => sum + (counts[id] ?? 0), 0);
  const sumAll = (counts: Record<string, number>) =>
    Object.values(counts).reduce((sum, n) => sum + n, 0);

  const active = sumIds(activeCounts.counts, openIds);
  // Both breakdowns are free: the two now-state queries already group by the
  // dimension each subset is cut on, so a subset is a second read of counts
  // that are in hand, never a second query.
  const activeInProgress = sumIds(activeCounts.counts, inProgressIds);
  const attentionAtRisk = attentionCounts.counts[AGE_STATUS.atRisk] ?? 0;
  const attentionOffTrack = attentionCounts.counts[AGE_STATUS.offTrack] ?? 0;
  const attention = attentionAtRisk + attentionOffTrack;
  const closed = sumIds(closedCounts.counts, doneIds);
  const updates = sumAll(updatesCounts.counts);

  const loading =
    (wantActive && !activeCounts.ready) ||
    (options.showAttention && !attentionCounts.ready) ||
    (wantClosed && !closedCounts.ready) ||
    (wantClosedPrev && !prevClosedCounts.ready) ||
    (options.showUpdates && !updatesCounts.ready) ||
    (wantUpdatesPrev && !prevUpdatesCounts.ready);

  const error =
    activeCounts.error ??
    attentionCounts.error ??
    closedCounts.error ??
    prevClosedCounts.error ??
    updatesCounts.error ??
    prevUpdatesCounts.error;

  return {
    active,
    activeInProgress,
    attention,
    attentionAtRisk,
    attentionOffTrack,
    closed,
    updates,
    updateCounts: updatesCounts.counts,
    closedPrev: wantClosedPrev ? sumIds(prevClosedCounts.counts, doneIds) : null,
    updatesPrev: wantUpdatesPrev ? sumAll(prevUpdatesCounts.counts) : null,
    openIds,
    doneIds,
    nowWhere,
    windowKey,
    windowRange,
    loading,
    error,
  };
}
